// doctor-frontend/src/pages/intern/ReturnedActivities.jsx
// Intern view: list of guided activities returned by JR/SR/Faculty.
// Intern can click "Resume" to reopen the guided form and edit/resubmit.
//
// PATCH (Jul 2026, no regression):
// - Core Practice Area-safe display handling.
// - Tenant slug fallback from storage so resume/back links keep /t/:slug.
// - Robust list parsing and returned/repeat status aliases.
// - Shows mapped rotation metadata, reviewer comments, and safe refresh without full-page reload.
// - Guided activities remain separate from citizen OPD visit workflow.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LaunchIcon from '@mui/icons-material/Launch';
import RefreshIcon from '@mui/icons-material/Refresh';
import { activityApi } from '../../services/activityApi';

const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const short = ['E', 'P', 'A'].join('\\.?');
  const full = ['Entrustable', 'Professional', 'Activities'].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value || 'null');
  } catch {
    return null;
  }
}

function coerceList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.activities)) return payload.activities;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.queue)) return payload.queue;
  if (Array.isArray(payload?.list)) return payload.list;
  return [];
}

function readTenantSlugFromStorage() {
  const candidates = [];

  try {
    [
      localStorage.getItem('tenantSlug'),
      localStorage.getItem('activeTenantSlug'),
      sessionStorage.getItem('tenantSlug'),
      sessionStorage.getItem('activeTenantSlug'),
    ]
      .filter(Boolean)
      .forEach((value) => candidates.push(value));

    [
      safeJsonParse(localStorage.getItem('loggedInUser')),
      safeJsonParse(sessionStorage.getItem('loggedInUser')),
      safeJsonParse(localStorage.getItem('user')),
      safeJsonParse(sessionStorage.getItem('user')),
    ]
      .filter(Boolean)
      .forEach((obj) => {
        const core = obj?.user || obj;
        candidates.push(
          core?.tenantSlug,
          obj?.tenantSlug,
          core?.tenant?.slug,
          obj?.tenant?.slug
        );
      });
  } catch {
    // ignore storage failures
  }

  return candidates
    .map((value) => String(value || '').trim())
    .find((slug) => /^[a-z0-9-]{2,80}$/i.test(slug)) || '';
}

function persistTenantSlug(slug) {
  try {
    const clean = String(slug || '').trim();
    if (!clean) return;
    localStorage.setItem('tenantSlug', clean);
    localStorage.setItem('activeTenantSlug', clean);
    sessionStorage.setItem('tenantSlug', clean);
    sessionStorage.setItem('activeTenantSlug', clean);
  } catch {
    // ignore storage failures
  }
}

function getTenantSlug() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/([^/]+)/);
    if (m) {
      const slug = decodeURIComponent(m[1]);
      persistTenantSlug(slug);
      return slug;
    }
  } catch {
    // ignore
  }

  return readTenantSlugFromStorage();
}

function tenantUrl(path) {
  const slug = getTenantSlug();
  const p = String(path || '/');
  if (!slug) return p;
  if (!p.startsWith('/')) {
    return `/t/${slug}/${p}`.replace(/\/{2,}/g, '/');
  }
  if (p.startsWith(`/t/${slug}`)) return p;
  return `/t/${slug}${p}`.replace(/\/{2,}/g, '/');
}

function normalizeStatusToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
    .replace(/-+/g, '-');
}

function statusText(value) {
  const s = normalizeStatusToken(value);

  if (s === 'returned' || s === 'observed-returned' || s === 'returned-to-edit') {
    return 'Returned for correction';
  }

  if (s === 'repeat' || s === 'observed-repeat' || s === 'repeat-required') {
    return 'Repeat required';
  }

  if (s === 'draft') return 'Draft';
  if (s === 'submitted' || s === 'pending' || s === 'observed-submitted') return 'Submitted';
  if (s === 'approved' || s === 'observed-approved') return 'Approved';

  return sanitizeDisplayText(value) || '-';
}

function statusChipColor(value) {
  const s = normalizeStatusToken(value);
  if (s.includes('repeat')) return 'warning';
  if (s.includes('return')) return 'default';
  if (s.includes('approve')) return 'success';
  if (s.includes('submit') || s.includes('pending')) return 'info';
  return 'default';
}

function isReturnedLike(row) {
  const status = normalizeStatusToken(row?.status || row?.workflowStatus || row?.approvalStatus || '');

  if ([
    'returned',
    'observed-returned',
    'observed-repeat',
    'repeat',
    'repeat-required',
    'returned-to-edit',
  ].includes(status)) {
    return true;
  }

  if (status.includes('return') || status.includes('repeat')) return true;

  const returnNote = sanitizeDisplayText(
    row?.returnNote ||
      row?.comments ||
      row?.observerComments ||
      row?.reviewerComments ||
      row?.decisionComments ||
      ''
  );

  return Boolean(returnNote && !['approved', 'observed-approved'].includes(status));
}

function formatDate(value) {
  if (!value) return '';
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return sanitizeDisplayText(value);
    return dt.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return sanitizeDisplayText(value);
  }
}

function activityId(row = {}) {
  return (
    row.activityInstanceId ||
    row.instanceId ||
    row.activityId ||
    row._id ||
    row.id ||
    ''
  );
}

function activityTitle(row = {}) {
  return sanitizeDisplayText(
    row.templateTitle ||
      row.activityTitle ||
      row.title ||
      row.templateName ||
      row.template?.title ||
      row.template?.name ||
      (row.templateSlug ? String(row.templateSlug).toUpperCase() : '') ||
      (row.slug ? String(row.slug).toUpperCase() : '') ||
      'Guided activity'
  );
}

function activityCentre(row = {}) {
  return sanitizeDisplayText(
    row.centreName ||
      row.centre ||
      row.chc ||
      row.location ||
      row.posting ||
      row.healthCentre ||
      ''
  );
}

function activityDate(row = {}) {
  return row.date || row.activityDate || row.createdAt || row.updatedAt || '';
}

function activityCorePracticeArea(row = {}) {
  const target = row.reportingTarget && typeof row.reportingTarget === 'object'
    ? row.reportingTarget
    : {};

  return sanitizeDisplayText(
    row.corePracticeAreaTitle ||
      row.practiceAreaTitle ||
      row.finalGroup ||
      target.section ||
      ''
  );
}

function activityRotationItem(row = {}) {
  const target = row.reportingTarget && typeof row.reportingTarget === 'object'
    ? row.reportingTarget
    : {};

  return sanitizeDisplayText(
    target.item ||
      row.reportingItem ||
      row.activityLabel ||
      row.rotationItem ||
      ''
  );
}

function returnFeedback(row = {}) {
  return sanitizeDisplayText(
    row.returnNote ||
      row.returnReason ||
      row.observerComments ||
      row.reviewerComments ||
      row.decisionComments ||
      row.comments ||
      row.reviewNote ||
      row.signoffNote ||
      row.notes ||
      ''
  );
}

function reviewerName(row = {}) {
  const reviewer =
    row.reviewerName ||
    row.observerName ||
    row.approverName ||
    row.returnedByName ||
    row.returnedBy ||
    row.reviewedBy ||
    row.observer ||
    row.reviewer ||
    '';

  if (!reviewer) return '';
  if (typeof reviewer === 'string') return sanitizeDisplayText(reviewer);
  if (typeof reviewer === 'object') {
    return sanitizeDisplayText(
      reviewer.name ||
        reviewer.fullName ||
        reviewer.displayName ||
        reviewer.email ||
        reviewer.username ||
        ''
    );
  }
  return sanitizeDisplayText(reviewer);
}

function getVisibleRows(rows) {
  return coerceList(rows)
    .filter(isReturnedLike)
    .sort((a, b) => new Date(activityDate(b) || 0) - new Date(activityDate(a) || 0));
}

export default function ReturnedActivities() {
  const navigate = useNavigate();
  const location = useLocation();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const returnTo = useMemo(() => {
    try {
      return `${location.pathname || ''}${location.search || ''}${location.hash || ''}`;
    } catch {
      return '';
    }
  }, [location.pathname, location.search, location.hash]);

  const activityLauncherPath = tenantUrl('/intern/activity/new');
  const activityLogPath = tenantUrl('/intern/activity/log');

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setErr('');

      const response = await activityApi.listMine({
        limit: 300,
        cb: Date.now(),
      });

      const returned = getVisibleRows(response);
      setRows(returned);
    } catch (e) {
      setErr(e?.message || 'Failed to load returned activities');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setErr('');

        const response = await activityApi.listMine({
          limit: 300,
          cb: Date.now(),
        });

        if (!alive) return;
        setRows(getVisibleRows(response));
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || 'Failed to load returned activities');
        setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const handleResume = (row) => {
    const id = activityId(row);

    if (!id) {
      setErr('Cannot resume: missing activity ID.');
      return;
    }

    navigate(tenantUrl(`/intern/activity/run/${encodeURIComponent(id)}`), {
      state: { from: returnTo },
    });
  };

  const handleOpenEvidence = (row) => {
    const id = activityId(row);

    if (!id) {
      setErr('Cannot open evidence: missing activity ID.');
      return;
    }

    navigate(tenantUrl(`/intern/activity/observe/${encodeURIComponent(id)}`), {
      state: { from: returnTo, viewOnly: true },
    });
  };

  return (
    <Box className="p-4 md:p-6 space-y-4">
      <Box>
        <Typography variant="h5" className="font-semibold">
          Returned Guided Activities
        </Typography>
        <Typography variant="body2" className="text-gray-600 dark:text-gray-300" sx={{ mt: 0.5 }}>
          These guided competency and skills activities have been returned by JR/SR/Faculty for correction.
          Resume the guided form, edit your entries, and submit again to the observer. Guided activities remain
          separate from the citizen OPD workflow.
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<LaunchIcon />}
          onClick={() => navigate(activityLauncherPath)}
        >
          Activity Launcher
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={() => navigate(activityLogPath)}
        >
          Activity Log
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={loadRows}
          disabled={loading}
        >
          Refresh
        </Button>
      </Stack>

      {err && (
        <Alert severity="error" onClose={() => setErr('')} className="mt-2">
          {err}
        </Alert>
      )}

      <Paper className="mt-4" variant="outlined">
        <Box className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <Box>
            <Typography variant="subtitle1" className="font-medium">
              Returned guided activities
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              Total requiring correction: {rows.length}
            </Typography>
          </Box>
          <Chip
            size="small"
            color={rows.length ? 'warning' : 'default'}
            label={`${rows.length} returned`}
            variant={rows.length ? 'filled' : 'outlined'}
          />
        </Box>

        {loading ? (
          <Box className="p-6 flex justify-center">
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Box className="p-6 text-center text-gray-500">
            No guided activities have been returned to you yet.
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Activity</TableCell>
                  <TableCell>{CORE_PRACTICE_AREA_LABEL}</TableCell>
                  <TableCell>Centre</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Feedback</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const id = activityId(row);
                  const title = activityTitle(row);
                  const centre = activityCentre(row);
                  const status = row.status || row.workflowStatus || row.approvalStatus || '';
                  const statusLabel = statusText(status);
                  const section = activityCorePracticeArea(row);
                  const item = activityRotationItem(row);
                  const feedback = returnFeedback(row);
                  const reviewer = reviewerName(row);
                  const rowKey = id || `${title}-${activityDate(row)}-${centre}`;

                  return (
                    <TableRow
                      key={rowKey}
                      hover
                    >
                      <TableCell>{formatDate(activityDate(row)) || '—'}</TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <span>{title}</span>
                          {id && (
                            <Typography variant="caption" sx={{ opacity: 0.65, fontFamily: 'monospace' }}>
                              {id}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {section || item ? (
                          <Stack spacing={0.25}>
                            {section && <span>{section}</span>}
                            {item && (
                              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                {item}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <span style={{ color: '#888' }}>—</span>
                        )}
                      </TableCell>
                      <TableCell>{centre || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={statusChipColor(status)}
                          label={statusLabel}
                        />
                      </TableCell>
                      <TableCell>
                        {feedback || reviewer ? (
                          <Stack spacing={0.25}>
                            {feedback && (
                              <Tooltip title={feedback}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: 320,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {feedback}
                                </Typography>
                              </Tooltip>
                            )}
                            {reviewer && (
                              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                Reviewer: {reviewer}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <span style={{ color: '#888' }}>—</span>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Open evidence read-only">
                            <span>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handleOpenEvidence(row)}
                              >
                                Evidence
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Resume guided form and resubmit">
                            <span>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<ReplayIcon />}
                                onClick={() => handleResume(row)}
                              >
                                Resume
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Divider />

      <Typography variant="caption" sx={{ opacity: 0.72 }}>
        Returned guided activities update the rotation report only after resubmission and observer approval.
      </Typography>
    </Box>
  );
}
